import { JobIdTypes } from "commonComponents/utilities/JobPicker/JobPicker.types";

import { JobStatus } from "entity/job/Job.api.types";

/** Minimal shape needed for General Job filtering logic */
export interface IGeneralJobFilterable {
    jobId: number;
    jobStatus: JobStatus;
}

export interface IGeneralJobFilterResult<T extends IGeneralJobFilterable> {
    filteredJobs: T[];
    isGeneralJobFallback: boolean;
}

/**
 * Filters the General job from job picker results based on whether the user has other accessible jobs.
 * When the user has real (non-General, non-placeholder) jobs, the General job is removed.
 * When the General job is the only real job available, it is kept and flagged as a fallback.
 *
 * @param jobs - The jobs returned from the job picker API
 * @param includeGeneralJob - Whether General job filtering should be applied
 * @returns The filtered jobs array and whether the General job is being used as a fallback
 */
export function filterGeneralJob<T extends IGeneralJobFilterable>(
    jobs: T[],
    includeGeneralJob: boolean,
): IGeneralJobFilterResult<T> {
    if (!includeGeneralJob) {
        return { filteredJobs: jobs, isGeneralJobFallback: false };
    }

    const realNonGeneralJobs = jobs.filter(
        (j) => j.jobId > 0 && j.jobStatus !== JobStatus.GeneralJob,
    );

    if (realNonGeneralJobs.length > 0) {
        return {
            filteredJobs: jobs.filter((j) => j.jobStatus !== JobStatus.GeneralJob),
            isGeneralJobFallback: false,
        };
    }

    const isGeneralJobFallback = jobs.some((j) => j.jobStatus === JobStatus.GeneralJob);
    return { filteredJobs: jobs, isGeneralJobFallback };
}

/**
 * Determines the selected job ID when the current job is not found in the job list.
 * Falls back to AllJobs for owner portal users, when the General job is the only option,
 * or when the page opts in via defaultToAllJobs; otherwise falls back to NoJobs.
 *
 * @param isOwnerPortal - Whether the current portal is the owner portal
 * @param isGeneralJobFallback - Whether the General job is being used as a fallback
 * @param defaultToAllJobs - Whether the page wants AllJobs as its no-job default
 * @returns The fallback job ID
 */
export function getFallbackSelectedJobId(
    isOwnerPortal: boolean,
    isGeneralJobFallback: boolean,
    defaultToAllJobs: boolean = false,
): number {
    if (isOwnerPortal || isGeneralJobFallback || defaultToAllJobs) {
        return JobIdTypes.AllJobs;
    }
    return JobIdTypes.NoJobs;
}

/**
 * Stale-while-revalidate predicate for the JobPicker loading overlay.
 * Returns true (= show overlay) only on the very first picker load when no
 * data exists yet. Subsequent reloads keep the existing picker rendered and
 * swap in fresh data on completion, avoiding a visible BTLoading flash.
 *
 * Used by `ListPageLayout.handleJobChange`. Extracted so the predicate can be
 * tested in isolation — the surrounding handler is hard to drive in a unit
 * test because it depends on a populated `jobPickerHandler`, builder context,
 * and full picker state.
 */
export function isJobPickerInitialLoad(jobPickerData: unknown): boolean {
    return jobPickerData === undefined;
}
